import { useAtomValue } from "jotai";
import { loadable } from "jotai/utils";
import { useEffect } from "react";

import { Logger } from "@moment/logging";

import { $documentIsEditable, $documentIsReadOnly } from "../../internal/document";
import { $document } from "../../internal/document-list";
import { CONFLICT_MARKER_REGEX } from "../../internal/document-metadata/errors";
import { useDocumentMetadata } from "./useDocumentMetadata";
import { useRouteParams } from "./useRouteParams";

const logger = new Logger("useDocumentFromCurrentRoute");

export const useDocumentFromCurrentRoute = () => {
	const { documentId } = useRouteParams();

	const documentListItem = useAtomValue(loadable($document(documentId)));
	const documentMetadata = useDocumentMetadata(documentId);

	const metadataErrorMessage =
		documentMetadata.error instanceof Error
			? documentMetadata.error.message
			: String(documentMetadata.error ?? "");
	const metadataErrorTag =
		documentMetadata.error &&
		typeof documentMetadata.error === "object" &&
		"tag" in documentMetadata.error
			? String(documentMetadata.error.tag)
			: undefined;
	const hasConflictMarkersInError = CONFLICT_MARKER_REGEX.test(metadataErrorMessage);
	const hasConflictTag = metadataErrorTag === "metadata-conflicted-error";
	const isMetadataConflict =
		!!documentMetadata.error && (hasConflictTag || hasConflictMarkersInError);

	useEffect(() => {
		if (documentMetadata.error) {
			if (isMetadataConflict) {
				logger.warn("Document metadata is in a conflicted state", {
					documentId,
					error: documentMetadata.error,
				});
			} else {
				logger.error("Error loading document metadata", {
					documentId,
					error: documentMetadata.error,
				});
			}
		}
	}, [documentId, documentMetadata.error, isMetadataConflict]);

	const isEditableLoadable = useAtomValue(loadable($documentIsEditable(documentId)));
	const isEditable = isEditableLoadable.state === "hasData" ? isEditableLoadable.data : false;

	const isReadOnly = useAtomValue($documentIsReadOnly(documentId));

	return {
		documentId: documentId || undefined,
		documentListItem:
			documentId && documentListItem.state === "hasData" ? documentListItem.data : undefined,
		documentMetadata:
			documentId && !documentMetadata.error ? documentMetadata.metadata : undefined,

		/* States */
		isEditable,
		isReadOnly,
		isLoading: documentMetadata.isLoading,
		error: documentMetadata.error,
		isError: documentMetadata.isError,
		isMetadataConflict,

		/* Actions */
		updateMetadata: documentMetadata.updateMetadata,
	};
};
